import {useState} from 'react';
import {StyleSheet} from 'react-native';
import {Modal, Portal} from 'react-native-paper';
import {useMutation, useQueryClient} from 'react-query';
import firestore, {
  FirebaseFirestoreTypes,
} from '@react-native-firebase/firestore';

import {ReservationYear} from '../../hooks/useDomainData';
import {useLayoutInfo} from '../../hooks/useLayoutInfo';
import {useAppSettings} from '../AppSettings';
import {
  DefaultFormikStoreValidator,
  FormikStoreForm,
  FormikStoreType,
  FormikStoreTypes,
} from '../FormikStore';
import {useAlerts} from 'react-native-paper-alerts';

export interface ReservationYearEditorProps {
  dialogVisible: boolean;
  setDialogVisible: (visible: boolean) => void;
  reservationYear?: ReservationYear;
}

export function ReservationYearEditor(
  props: ReservationYearEditorProps,
): JSX.Element {
  const queryClient = useQueryClient();
  const Alert = useAlerts();

  const layoutInfo = useLayoutInfo();
  const appSettings = useAppSettings();

  const [validating, setValidating] = useState(false);

  const collectionName = 'reservationYears';
  const saveFirestoreObject = useMutation(
    (firestoreObject: ReservationYear) => {
      if (props.reservationYear === undefined) {
        // console.log(
        //   'adding: ' + JSON.stringify(firestoreObject, null, 2),
        // );

        // If there was no date set, delete the date property totally

        return (
          firestore()
            .collection(collectionName)
            // the types of .add and .set are incompatible, but we don't need the return value
            .add(firestoreObject) as Promise<unknown> as Promise<void>
        );
      }

      // console.log(
      //   'saving: ' + JSON.stringify(firestoreObject, null, 2),
      // );
      return firestore()
        .collection(collectionName)
        .doc(props.reservationYear?.ref.id)
        .set(firestoreObject, {merge: true});
    },
    {
      onSettled: () => {
        // console.log('saving succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
      onError: e => {
        Alert.alert('Error', 'There was an error while saving: ' + e, [
          {text: appSettings.t('OK')},
        ]);
      },
    },
  );

  const themedStyles = StyleSheet.create({
    formikStoreContent: {
      margin: 60,
      // flex: 1,
      borderColor: appSettings.currentTheme.colors.border,
      borderWidth: 2,
      backgroundColor: appSettings.currentTheme.colors.background,
      position: 'absolute',
      maxWidth: layoutInfo.screen.width - 120,
      maxHeight: layoutInfo.screen.height - 160,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  });

  let reservationYear: {[key: string]: FormikStoreType} = {
    reservationYearId: Date.now(),
    name: undefined,
    startDate: undefined,
    endDate: undefined,
  };

  if (props.reservationYear !== undefined) {
    console.log(
      'props.reservationYear is defined, it is ' +
        JSON.stringify(props.reservationYear, null, 2),
    );
    reservationYear.reservationYearId = props.reservationYear.reservationYearId;
    reservationYear.name = props.reservationYear.name;
    if (props.reservationYear.startDate !== undefined) {
      reservationYear.startDate = (
        props.reservationYear.startDate as FirebaseFirestoreTypes.Timestamp
      )?.toDate();
    }
    if (props.reservationYear.endDate !== undefined) {
      reservationYear.endDate = (
        props.reservationYear.endDate as FirebaseFirestoreTypes.Timestamp
      )?.toDate();
    }
  }

  return (
    <Portal>
      <Modal
        contentContainerStyle={themedStyles.formikStoreContent}
        visible={props.dialogVisible}
        onDismiss={() => props.setDialogVisible(false)}>
        <FormikStoreForm
          objects={[
            {
              title: `${
                props.reservationYear ? 'Edit' : 'Create'
              } Reservation Year`,
              firestoreObject: reservationYear,
              fieldMetadata: [
                {
                  key: 'reservationYearId',
                  name: 'Reservation Year ID',
                  type: FormikStoreTypes.number,
                  required: true,
                  editable: false,
                  errorText: 'Reservation Year ID is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'name',
                  name: 'Name',
                  type: FormikStoreTypes.text,
                  required: true,
                  errorText: 'Name is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'startDate',
                  name: 'Start Date',
                  type: FormikStoreTypes.date,
                  required: true,
                  errorText: 'Select the first day of reservation year',
                  datesUTC: true,
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'endDate',
                  name: 'End Date',
                  type: FormikStoreTypes.date,
                  required: true,
                  errorText: 'Select the last day of reservation year',
                  datesUTC: true,
                  validator: DefaultFormikStoreValidator,
                },
              ],
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              validate: false,
              onPress: () => {
                props.setDialogVisible(false);
              },
            },
            {
              text: 'Save',
              validate: true,
              onPress: formObjects => {
                // console.log(
                //   'saving with ' +
                //     JSON.stringify(formObjects[0].firestoreObject, null, 2),
                // );
                setValidating(true);
                saveFirestoreObject.mutate(
                  formObjects[0].firestoreObject as unknown as ReservationYear,
                );
                props.setDialogVisible(false);
              },
            },
          ]}
          validateForm={validating}
        />
      </Modal>
    </Portal>
  );
}
